import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getMeasurements, createMeasurement, deleteMeasurement, getCustomers } from '@/lib/api';
import { Plus, Search, Trash2, Ruler, Eye } from 'lucide-react';
import { format } from 'date-fns';

const measurementFields = [
  { key: 'neck_circumference', label: 'Neck' },
  { key: 'chest_circumference', label: 'Chest' },
  { key: 'waist_circumference', label: 'Waist' },
  { key: 'hip_circumference', label: 'Hip' },
  { key: 'shoulder_width', label: 'Shoulder' },
  { key: 'sleeve_length', label: 'Sleeve' },
  { key: 'arm_circumference', label: 'Arm' },
  { key: 'wrist_circumference', label: 'Wrist' },
  { key: 'inseam', label: 'Inseam' },
  { key: 'outseam', label: 'Outseam' },
  { key: 'thigh_circumference', label: 'Thigh' },
  { key: 'back_length', label: 'Back Length' },
];

const MeasurementsPage = () => {
  const [measurements, setMeasurements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    unit: 'inches',
    notes: '',
    ...Object.fromEntries(measurementFields.map(f => [f.key, ''])),
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [measurementsRes, customersRes] = await Promise.all([
        getMeasurements(),
        getCustomers(),
      ]);
      setMeasurements(measurementsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        ...Object.fromEntries(
          measurementFields.map(f => [f.key, formData[f.key] ? parseFloat(formData[f.key]) : null])
        ),
      };
      await createMeasurement(data);
      toast.success('Measurement recorded successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create measurement');
    }
  };

  const handleDelete = async (measurementId) => {
    if (!window.confirm('Are you sure you want to delete this measurement?')) return;
    try {
      await deleteMeasurement(measurementId);
      toast.success('Measurement deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete measurement');
    }
  };

  const handleView = (measurement) => {
    setSelectedMeasurement(measurement);
    setViewDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      unit: 'inches',
      notes: '',
      ...Object.fromEntries(measurementFields.map(f => [f.key, ''])),
    });
  };

  const filteredMeasurements = measurements.filter((m) =>
    m.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8 p-3" data-testid="measurements-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Measurements
          </h1>
          <p className="text-muted-foreground">Record and manage customer measurements</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow"
              data-testid="add-measurement-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Measurement
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                Record Measurements
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-uppercase">Customer *</Label>
                  <Select
                    value={formData.customer_id}
                    onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="measurement-customer-select" className="h-11 bg-secondary/50">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.customer_id} value={customer.customer_id}>
                          {customer.customer_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Unit</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger data-testid="measurement-unit-select" className="h-11 bg-secondary/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inches">Inches</SelectItem>
                      <SelectItem value="cm">Centimeters</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Body Measurements</h3>
                <div className="grid grid-cols-3 gap-4">
                  {measurementFields.map((field) => (
                    <div key={field.key} className="space-y-2">
                      <Label className="text-xs text-muted-foreground">{field.label}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        data-testid={`measurement-${field.key}-input`}
                        value={formData[field.key]}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        placeholder="0.0"
                        className="h-10 bg-secondary/50"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-uppercase">Notes</Label>
                <Textarea
                  data-testid="measurement-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="bg-secondary/50 min-h-[80px]"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-measurement-button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Save Measurements
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="measurement-search-input"
          placeholder="Search by customer name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-11 bg-secondary/50"
        />
      </div>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Measurement Details
            </DialogTitle>
          </DialogHeader>
          {selectedMeasurement && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Customer:</span>
                  <p className="font-medium">{selectedMeasurement.customer_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Unit:</span>
                  <p className="font-medium">{selectedMeasurement.unit}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {measurementFields.map((field) => (
                  <div key={field.key} className="p-3 rounded-sm bg-secondary/50">
                    <span className="text-xs text-muted-foreground">{field.label}</span>
                    <p className="font-medium">
                      {selectedMeasurement[field.key] ?? '—'}
                    </p>
                  </div>
                ))}
              </div>
              {selectedMeasurement.notes && (
                <div>
                  <span className="text-sm text-muted-foreground">Notes:</span>
                  <p className="text-sm mt-1">{selectedMeasurement.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Table */}
      <Card className="border-white/10 bg-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredMeasurements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Ruler className="w-12 h-12 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-1">No measurements found</h3>
              <p className="text-muted-foreground text-sm">
                {searchTerm ? 'Try a different search term' : 'Record your first measurement to get started'}
              </p>
            </div>
          ) : (
            <Table className="table-luxury">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Chest</TableHead>
                  <TableHead>Waist</TableHead>
                  <TableHead>Shoulder</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMeasurements.map((measurement) => (
                  <TableRow key={measurement.measurement_id} className="table-row-hover">
                    <TableCell className="font-medium">{measurement.customer_name || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(measurement.measurement_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>{measurement.unit}</TableCell>
                    <TableCell>{measurement.chest_circumference ?? '—'}</TableCell>
                    <TableCell>{measurement.waist_circumference ?? '—'}</TableCell>
                    <TableCell>{measurement.shoulder_width ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleView(measurement)}
                          data-testid={`view-measurement-${measurement.measurement_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(measurement.measurement_id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-measurement-${measurement.measurement_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MeasurementsPage;
